/**
 * LeadInformation.jsx — full-page lead view matching the owner mockup
 * (2026-08-27). Replaces the right-side drawer for editing; the drawer
 * stays for the quick-peek use case.
 *
 * Layout: header (name + status badge + actions) → tabs → 4×2 grid of
 * cards (Property Lookup + Seller Contact ▲ Seller Motivation + Property
 * Condition ▲ Timeline & Price + Decision Maker & Occupancy ▲ Follow-Up
 * + CRM Status) → Conversation Notes (team-editable).
 *
 * Only admins can edit the top form fields; anyone on the team can
 * append a team note via /api/leads/{id}/append-note.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronRight, MapPin, User, Flame, Wrench, Calendar,
  ClipboardList, CheckCircle2, Send, PhoneCall, Save,
  Lock, Plus, Loader2, MessageSquare,
} from "lucide-react";

export default function LeadInformation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const isAdmin = !!(user?.is_admin) || user?.role === "admin" || user?.role === "owner" || can("lead.edit");

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [appending, setAppending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leads/${id}`);
        setLead(data);
      } catch { toast.error("Lead not found"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const patch = (field, val) => setLead({ ...lead, [field]: val });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, lead);
      setLead(data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setAppending(true);
    try {
      await api.post(`/leads/${id}/append-note`, { note });
      const { data } = await api.get(`/leads/${id}`);
      setLead(data); setNote("");
      toast.success("Note added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Add note failed"); }
    finally { setAppending(false); }
  };

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading lead…</div>;
  if (!lead) return <div className="p-12 text-center text-muted-foreground">Lead not found.</div>;

  const gate = { disabled: !isAdmin, "aria-disabled": !isAdmin };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="lead-info-page">
      {/* Breadcrumb */}
      <div className="text-xs flex items-center gap-1.5 text-fuchsia-600 font-medium">
        <button onClick={() => navigate("/boards")} className="hover:underline">Leads</button>
        <ChevronRight size={12} />
        <span className="text-slate-500">Lead Information</span>
      </div>

      {/* Header */}
      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm p-4 md:p-5 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-fuchsia-100 flex items-center justify-center">
            <User size={22} className="text-fuchsia-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-800">{lead.name || "Unnamed Lead"}</h1>
              {lead.motivation_level && (
                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest border-2 ${
                  lead.motivation_level === "hot" ? "border-red-300 bg-red-50 text-red-700"
                  : lead.motivation_level === "warm" ? "border-amber-300 bg-amber-50 text-amber-700"
                  : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                  {lead.motivation_level === "hot" ? "🔥 Hot" : lead.motivation_level === "warm" ? "Warm" : "Cold"} Lead
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Lead ID: {lead.id?.slice(0, 8)} · Created {lead.created_at?.slice(0, 10) || "—"}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={() => navigate(`/boards?lead_id=${id}`)}
            data-testid="lead-info-send-to-acq">
            <Send size={14} className="mr-1.5" /> Send to Acquisitions
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/dialer?lead_id=${id}`)}
            data-testid="lead-info-save-call">
            <PhoneCall size={14} className="mr-1.5" /> Save & Call
          </Button>
          <Button variant="outline" onClick={save} disabled={saving || !isAdmin}
            data-testid="lead-info-save-followup">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Calendar size={14} className="mr-1.5" />}
            Save & Follow Up
          </Button>
        </div>
      </div>

      {/* Admin-only badge */}
      {!isAdmin && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 flex items-center gap-1.5" data-testid="lead-info-admin-only">
          <Lock size={12} /> Only admins can edit lead information. You can still add notes and follow-up actions below.
        </div>
      )}

      {/* 4×2 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Property Lookup */}
        <Card icon={<MapPin size={16} className="text-fuchsia-500" />} title="Property Lookup"
              subtitle="Start here — every downstream question fills in from what we find.">
          <Input value={lead.property_address || ""} onChange={(e) => patch("property_address", e.target.value)}
                 placeholder="123 Main St, Cape Coral, FL" {...gate} data-testid="lead-info-property-address" />
          <div className="mt-3 rounded-xl border-2 border-fuchsia-200 bg-fuchsia-50/40 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 mb-2">Property Snapshot</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <Snap label="Owner" value={lead.owner_name} />
              <Snap label="Property Address" value={lead.property_address} />
              <Snap label="ARV (Est.)" value={lead.arv ? money(lead.arv) : lead.property_value ? money(lead.property_value) : "—"} />
              <Snap label="Equity" value={lead.equity ? money(lead.equity) : "—"} />
              <Snap label="Beds / Baths" value={[lead.bedrooms, lead.bathrooms].filter(Boolean).join(" / ") || "—"} />
              <Snap label="Sq Ft" value={lead.sqft || "—"} />
            </div>
          </div>
        </Card>

        {/* Seller Contact */}
        <Card icon={<User size={16} className="text-sky-500" />} title="Seller Contact">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" value={lead.first_name} onChange={(v) => patch("first_name", v)} gate={gate} />
            <Field label="Last name" value={lead.last_name || lead.name} onChange={(v) => patch("last_name", v)} gate={gate} />
            <Field label="Primary phone" value={lead.phone} onChange={(v) => patch("phone", v)} gate={gate} testid="lead-info-phone" />
            <Field label="Secondary phone" value={lead.phone_2} onChange={(v) => patch("phone_2", v)} gate={gate} />
            <Field label="Email" value={lead.email} onChange={(v) => patch("email", v)} gate={gate} />
            <Field label="Lead source" value={lead.source_tag} onChange={(v) => patch("source_tag", v)} gate={gate} />
          </div>
        </Card>

        {/* Seller Motivation */}
        <Card icon={<Flame size={16} className="text-red-500" />} title="Seller Motivation">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Why are you considering selling?</Label>
          <Textarea rows={3} value={lead.motivation || ""} onChange={(e) => patch("motivation", e.target.value)}
                    className="mt-1" {...gate} data-testid="lead-info-motivation" />
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-3 block">Motivation Level</Label>
          <select value={lead.motivation_level || ""} onChange={(e) => patch("motivation_level", e.target.value)}
                  className="mt-1 w-full border-2 border-slate-200 rounded-md px-3 py-2 text-sm bg-white" {...gate}>
            <option value="">—</option>
            <option value="hot">🔥 Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
        </Card>

        {/* Property Condition */}
        <Card icon={<Wrench size={16} className="text-amber-500" />} title="Property Condition">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Overall condition" value={lead.condition_overall} onChange={(v) => patch("condition_overall", v)} gate={gate} />
            <Field label="Roof" value={lead.condition_roof} onChange={(v) => patch("condition_roof", v)} gate={gate} />
            <Field label="AC" value={lead.condition_ac} onChange={(v) => patch("condition_ac", v)} gate={gate} />
            <Field label="Kitchen & Bathrooms" value={lead.condition_kb} onChange={(v) => patch("condition_kb", v)} gate={gate} />
          </div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-3 block">
            Condition Notes (what did the seller say?)
          </Label>
          <Textarea rows={3} value={lead.condition_notes || ""} onChange={(e) => patch("condition_notes", e.target.value)}
                    className="mt-1" {...gate} />
        </Card>

        {/* Timeline & Price */}
        <Card icon={<Calendar size={16} className="text-emerald-500" />} title="Timeline & Price">
          <div className="grid grid-cols-2 gap-3">
            <Field label="When would you like to sell?" value={lead.timeline_bucket} onChange={(v) => patch("timeline_bucket", v)} gate={gate} />
            <Field label="Why that timeline?" value={lead.timeline_notes} onChange={(v) => patch("timeline_notes", v)} gate={gate} />
            <Field label="Asking Price ($)" value={lead.asking_price} onChange={(v) => patch("asking_price", v)} gate={gate} />
            <Field label="Flexible?" value={lead.price_flexible} onChange={(v) => patch("price_flexible", v)} gate={gate} />
            <Field label="Lowest mentioned ($)" value={lead.lowest_price_mentioned} onChange={(v) => patch("lowest_price_mentioned", v)} gate={gate} />
          </div>
        </Card>

        {/* Decision Maker & Occupancy */}
        <Card icon={<ClipboardList size={16} className="text-violet-500" />} title="Decision Maker & Occupancy">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Anyone else involved?" value={lead.decision_shared} onChange={(v) => patch("decision_shared", v)} gate={gate} />
            <Field label="Name / Relationship" value={lead.decision_maker_name} onChange={(v) => patch("decision_maker_name", v)} gate={gate} />
            <Field label="Property currently" value={lead.occupancy} onChange={(v) => patch("occupancy", v)} gate={gate} />
            <Field label="Tenant situation" value={lead.tenant_situation} onChange={(v) => patch("tenant_situation", v)} gate={gate} />
          </div>
        </Card>

        {/* Follow-Up */}
        <Card icon={<Calendar size={16} className="text-sky-500" />} title="Follow-Up">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Next action" value={lead.next_action} onChange={(v) => patch("next_action", v)} gate={gate} />
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">When</Label>
              <Input type="datetime-local" value={lead.next_followup_at || ""}
                     onChange={(e) => patch("next_followup_at", e.target.value)} className="mt-1" {...gate} />
            </div>
          </div>
          <Textarea rows={3} value={lead.followup_notes || ""} onChange={(e) => patch("followup_notes", e.target.value)}
                    placeholder="Follow-up notes" className="mt-3" {...gate} />
        </Card>

        {/* CRM Status */}
        <Card icon={<CheckCircle2 size={16} className="text-emerald-500" />} title="CRM Status">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lead Status" value={lead.stage} onChange={(v) => patch("stage", v)} gate={gate} />
            <Field label="Assigned to (optional)" value={lead.assigned_to} onChange={(v) => patch("assigned_to", v)} gate={gate} />
          </div>
        </Card>
      </div>

      {/* Conversation Notes */}
      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-slate-500" />
            <h3 className="font-semibold text-slate-800">Conversation Notes</h3>
            <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              Team Editable
            </span>
          </div>
        </div>
        <div className="flex gap-2 mb-3">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Add any additional information, call notes, and updates."
                    data-testid="lead-info-add-note" />
          <Button onClick={addNote} disabled={appending || !note.trim()} className="bg-fuchsia-600 hover:bg-fuchsia-700 self-start"
            data-testid="lead-info-add-note-btn">
            {appending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
            Add Note
          </Button>
        </div>
        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-80 overflow-y-auto bg-slate-50 rounded-md p-3 border">
          {lead.notes || "No notes yet — be the first to add one."}
        </pre>
      </div>

      {/* Footer */}
      <div className="text-[11px] text-center text-slate-500 py-2 flex items-center justify-center gap-1.5">
        <Lock size={11} /> Only admins can edit lead information. Team members can add notes and follow-up actions.
      </div>
    </div>
  );
}

function Card({ icon, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm">
      <header className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
        {icon}
        <h2 className="font-semibold text-slate-800 text-sm">{title}</h2>
        {subtitle && <span className="text-[10px] text-muted-foreground ml-2">{subtitle}</span>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, gate, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" {...gate} data-testid={testid} />
    </div>
  );
}

function Snap({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="text-xs font-medium text-slate-800 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}
